"use client";

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";

import type { AppThemeTone } from "@/components/editorial/product-bridge/app-surface";

import { tourScrolledAt, tourTimeline, tourViewAt } from "./channel-tour";
import { ShotChannelWindow, type ShotChannelView, type ShotChannelViews } from "./channel-window";
import { ReplayControls } from "./replay-controls";
import { useReplayClock } from "./use-replay-clock";
import type { ShotWorkspace } from "./workspace";

type TourProps = {
  workspace: ShotWorkspace;
  views: ShotChannelViews;
  /** The views in the order the tour switches through them. */
  order: readonly ShotChannelView[];
  tone: AppThemeTone;
};

/**
 * The tour's player: the channel on each view in turn, switched through the
 * view picker as the timeline says, on the replays' clock (it pauses off
 * screen and on request, and replays from the start). A calendar taller
 * than the frame scrolls to its first week with records, and a dashboard
 * down through its lower widgets and back. With
 * reduced motion it rests on the last view until asked to play.
 */
export function ChannelTourPlayer({ workspace, views, order, tone, reduceMotion }: TourProps & { reduceMotion: boolean }) {
  const timeline = useMemo(() => tourTimeline(order), [order]);
  const { ref, atMs, playing, ended, toggle } = useReplayClock(timeline.endsMs, reduceMotion);
  // The timeline holds only the views in `order`, each one the window shows.
  const view = tourViewAt(timeline, atMs) as ShotChannelView;
  const scrolled = tourScrolledAt(timeline, atMs);
  const frameRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const behavior = reduceMotion ? "auto" : "smooth";
    // The dashboard's lens around its layout grid scrolls to its end.
    const dashboard = frame.querySelector(".mlx-dashboard-layout-container")?.closest<HTMLElement>(".overflow-auto");
    if (dashboard) {
      dashboard.scrollTo({ top: scrolled ? dashboard.scrollHeight : 0, behavior });
      return;
    }
    // The calendar's month scrolls its first week with records to the top.
    const month = frame.querySelector("[data-calendar-day]")?.closest<HTMLElement>(".overflow-auto");
    if (month) month.scrollTo({ top: scrolled ? firstRecordsWeekTop(month) : 0, behavior });
  }, [scrolled, view, reduceMotion]);
  return (
    <div ref={ref}>
      <div ref={frameRef} className={!playing && !ended && atMs > 0 ? "[&_*]:[animation-play-state:paused]" : undefined}>
        <ShotChannelWindow tone={tone} workspace={workspace} {...views} view={view} inert aria-hidden="true" />
      </div>
      <ReplayControls
        state={ended ? "ended" : playing ? "playing" : "paused"}
        progress={atMs / timeline.endsMs}
        onToggle={toggle}
      />
    </div>
  );
}

/** How far down a month its first week with records sits, beneath the sticky weekday headings. */
function firstRecordsWeekTop(month: HTMLElement): number {
  // An event is a button titled with its record; a day's add button is not titled.
  const day = month.querySelector<HTMLElement>("[data-calendar-day]:has(button[title])");
  if (!day) return 0;
  const weekdays = month.firstElementChild instanceof HTMLElement ? month.firstElementChild.offsetHeight : 0;
  return month.scrollTop + day.getBoundingClientRect().top - month.getBoundingClientRect().top - weekdays;
}

/**
 * Holds the tour's first frame, rendered on the server, until the player
 * starts from it: when a third of the frame is in view. With reduced motion
 * the player mounts at once, resting on its last view.
 */
export function ChannelTourIsland({ children, ...props }: TourProps & { children: ReactNode }) {
  const islandRef = useRef<HTMLDivElement>(null);
  const [player, setPlayer] = useState<{ reduceMotion: boolean } | null>(null);

  useEffect(() => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const island = islandRef.current;
    if (reduceMotion || !island || typeof IntersectionObserver === "undefined") {
      setPlayer({ reduceMotion });
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        setPlayer({ reduceMotion });
      },
      { threshold: 0.35 },
    );
    observer.observe(island);
    return () => observer.disconnect();
  }, []);

  return <div ref={islandRef}>{player ? <ChannelTourPlayer {...props} reduceMotion={player.reduceMotion} /> : children}</div>;
}
